import PropTypes from 'prop-types'
import React, {useEffect, useMemo} from 'react'
import {Box} from '@chakra-ui/react'
import Arlink from './arlink'
import {useProduct} from '@salesforce/commerce-sdk-react'
import {productUrlBuilder} from '../../utils/url'
import {isProductSet} from '../../utils/product-utils'
import {useProductSetPrice} from '../../hooks/useScapiCustomApi'
import {isServer} from '../../utils/utils'
import {useIntl} from 'react-intl'
import ProductDisplayPrice from '../../components/product-display-price'
import HybridLink from '../../components/hybrid-link'
import {HYBRID_PDP} from '../../constants'
import useIntersectionObserver from '../../hooks/use-intersection-observer'

/**
 * Display ProductInfos (name & price)
 *
 * @param {string} pid - productId
 * @param {string} variant - `default`: name & price, `onlyPrice`: price
 * @param {'lazy'|'eager'} [loading] - Lazy load the widget or render it immediately
 *
 * @example
 *   <pwa-widget-productinfos pid="5968">
 *     $include(...)$
 *   </pwa-widget-productinfos>
 */
const WidgetProductinfos = ({pid, variant, loading = 'lazy'}) => {
    const PRODUCT_EXPAND_PER_VARIANT = {
        arLink: ['variations'],
        arLinkPrimaryButton: ['variations'],
        arLinkSecondaryButton: ['variations'],
        onlyPrice: ['prices', 'promotions'],
        default: ['prices', 'promotions']
    }

    const {locale} = useIntl()
    // flag that indicates if the widget was displayed to trigger loading of data
    const [setRef, wasDisplayed] = useIntersectionObserver({
        useOnce: true,
        initialInView: loading === 'eager',
        skip: loading === 'eager'
    })
    const {
        error: productError,
        data: product,
        isLoading
    } = useProduct(
        {
            parameters: {
                id: pid,
                // based on the variant, we expand different fields
                expand: PRODUCT_EXPAND_PER_VARIANT[variant] || PRODUCT_EXPAND_PER_VARIANT.default
            }
        },
        {enabled: !!pid && !!wasDisplayed}
    )
    useEffect(() => {
        if (productError) {
            console.error(`Failed to load product ${pid}`, productError)
        }
    }, [pid, productError])

    // load set prices
    // promotions do not appear when loading product set with getProduct, so we need to fetch it in any case
    const needsFetchingCustomerPrice =
        Boolean(pid) && Boolean(product) && isProductSet(product) && !variant?.includes('arLink')
    const {
        data: productSetPricesData,
        error: productSetPricesError,
        isInitialLoading: isProductSetPricesLoading
    } = useProductSetPrice(
        {
            parameters: {
                locale,
                c_pid: pid
            }
        },
        {
            // fetch only on client to include customer prices
            enabled: needsFetchingCustomerPrice && !isServer
        }
    )
    useEffect(() => {
        if (productSetPricesError) {
            console.error(`Failed to load product set prices for ${pid}`, productSetPricesError)
        }
    }, [pid, productSetPricesError])
    const isPriceLoading = needsFetchingCustomerPrice && isProductSetPricesLoading

    const productWithPrice = useMemo(() => {
        if (needsFetchingCustomerPrice) {
            return {
                ...product,
                ...productSetPricesData?.resp
            }
        }
        return product
    }, [needsFetchingCustomerPrice, product, productSetPricesData])

    if (!pid || productError || productSetPricesError) {
        return null
    }

    if (isLoading) {
        // TODO: add skeleton loader
        return <Box ref={setRef} />
    }

    // prevent initial flash of undefined product
    if (!productWithPrice) {
        return null
    }

    switch (variant) {
        case 'arLink':
        case 'arLinkPrimaryButton':
        case 'arLinkSecondaryButton':
            return <Arlink product={productWithPrice} variant={variant} />
        case 'onlyPrice':
            return (
                <ProductDisplayPrice
                    product={productWithPrice}
                    loading={isPriceLoading}
                    variant="productTile"
                    data-testid="product-tile-price"
                />
            )
        default:
            return (
                <Box pr={4} color="stokkeCore.black">
                    <HybridLink
                        page={HYBRID_PDP}
                        to={productUrlBuilder(productWithPrice, locale)}
                        pr={1}
                        width="100%"
                        size="sm"
                        display="inline"
                        verticalAlign="middle"
                    >
                        {productWithPrice.name}
                    </HybridLink>
                    <Box as="span" display="inline-block" width="max-content">
                        <ProductDisplayPrice
                            product={productWithPrice}
                            loading={isPriceLoading}
                            variant="productTile"
                            data-testid="product-tile-price"
                            display="flex"
                            verticalAlign="middle"
                        />
                    </Box>
                </Box>
            )
    }
}

WidgetProductinfos.propTypes = {
    pid: PropTypes.string,
    variant: PropTypes.string,
    loading: PropTypes.oneOf(['lazy', 'eager'])
}

export default WidgetProductinfos
